import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { PageViewer } from '../../components/PageViewer';
import {
  PATH_RESOLVABLE_MISSION_TYPE_GENERAL,
  PATH_RESOLVABLE_MISSION_TYPE_GENERAL_EDIT,
} from '../../app/constants/paths.constant';
import { FormVariant } from '../../forms';
import {
  ResolvableMissionTypeForm,
  ResolvableMissionTypeFormTab,
} from '../../forms/ResolvableMissionType';
import { Code } from '../../components/Code';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { MissionCategoryPath } from '../../components/MissionCategoryPath';

export const ResolvableMissionTypePage_GeneralInfo_ResolvableMissionType_Fragment =
  graphql(`
    fragment ResolvableMissionTypePage_GeneralInfo_ResolvableMissionType_Fragment on ResolvableMissionType {
      _id
      codingConfig {
        kind
        strategyId
        ... on CodingConfigManual {
          code
        }
        ...CodeCmp_CodingConfig_Fragment
      }
      code
      label
      title
      categoryParentId
      categoryLeaf
      content
      ...ResolvableMissionTypeForm_ResolvableMissionType_Fragment
    }
  `);

interface Props {
  readonly resolvableMissionType: FragmentType<
    typeof ResolvableMissionTypePage_GeneralInfo_ResolvableMissionType_Fragment
  >;
}

export const GeneralInfo = ({
  resolvableMissionType: pResolvableMissionType,
}: Readonly<Props>) => {
  const resolvableMissionType = getFragmentData(
    ResolvableMissionTypePage_GeneralInfo_ResolvableMissionType_Fragment,
    pResolvableMissionType,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_RESOLVABLE_MISSION_TYPE_GENERAL,
      edit: PATH_RESOLVABLE_MISSION_TYPE_GENERAL_EDIT,
    },
    { id: resolvableMissionType._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Typography gutterBottom variant="h6">
          Type de Mission
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Code
              code={resolvableMissionType.code}
              codingConfig={resolvableMissionType.codingConfig}
            />
            <Typography gutterBottom variant="body1">
              Libellé : {resolvableMissionType.label}
            </Typography>
            <Typography gutterBottom variant="body1">
              Catégorie :{' '}
              <MissionCategoryPath
                categoryLeaf={resolvableMissionType.categoryLeaf}
                categoryParentId={resolvableMissionType.categoryParentId}
              />
            </Typography>
          </Box>
        </Paper>
        <Typography gutterBottom variant="h6">
          Document
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            {/*eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing*/}
            {resolvableMissionType.title || (
              <Typography gutterBottom variant="body1">
                Titre non renseigné
              </Typography>
            )}
          </Box>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            {resolvableMissionType.content ? (
              <PageViewer html={resolvableMissionType.content} />
            ) : (
              <Typography gutterBottom variant="body1">
                Contenu non renseigné
              </Typography>
            )}
          </Box>
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editMissionTypeGeneralInfo',
            icon: <EditIcon />,
            name: 'Modifier Type de Mission',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations Générales du Type de Mission"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <ResolvableMissionTypeForm
          formVariant={FormVariant.Modification}
          initialValues={resolvableMissionType}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          resolvableMissionTypeId={resolvableMissionType._id}
          stepIdsSubset={[ResolvableMissionTypeFormTab.GeneralInfo]}
          title="Formulaire de modification des Informations Générales du Type de Mission"
        />
      </RightDrawer>
    </>
  );
};
